// C9 — Invite Collaborator modal.
//
// Opens from the Share modal's "Invite People" button. Posts to the
// C4 invite endpoint (POST /api/{kind}s/{id}/invite). Maps backend
// error codes to inline copy via ERROR_COPY.
//
// Email validation is intentionally minimal — the backend is the
// authority (EmailStr Pydantic type). We just guard against the
// empty / no-`@` cases so the Send button stays disabled when the
// input is obviously incomplete.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Mail, X } from 'lucide-react';
import { Button } from '../ui/button';
import { collabApi, ERROR_COPY } from '../../lib/collabApi';

const isProbablyEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());

export default function InviteCollaboratorModal({
  open,
  onClose,
  // "design" | "template" — singular, collabApi appends the plural.
  resourceKind,
  resourceId,
  // Optional — surfaces a friendlier title.
  resourceName,
  // Notify parent so it can refresh the People With Access list.
  onInvited = null,
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('editor');
      setError(null);
      // Tick for the modal mount before focusing — Radix focus traps
      // can race with this otherwise.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!isProbablyEmail(email)) {
      setError({ message: 'Enter a valid email address.' });
      return;
    }
    setError(null);
    setSending(true);
    try {
      const created = await collabApi.invite(resourceKind, resourceId, {
        email: email.trim(),
        role,
      });
      onInvited?.(created);
      onClose?.();
    } catch (err) {
      setError({
        code: err.code,
        message: ERROR_COPY[err.code] || err.message || 'Could not send the invitation.',
      });
    } finally {
      setSending(false);
    }
  }, [email, role, resourceKind, resourceId, onInvited, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="invite-modal-backdrop"
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        data-testid="invite-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Invite Collaborator</h2>
            {resourceName && (
              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[18rem]">
                {resourceName}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 -mt-1 -mr-1 p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <label htmlFor="invite-email" className="block text-xs font-semibold text-gray-600 mb-1.5">
          Email Address
        </label>
        <div className="relative mb-4">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@example.com"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            data-testid="invite-email-input"
            autoComplete="email"
          />
        </div>

        <p className="text-xs font-semibold text-gray-600 mb-1.5">Role</p>
        <div className="space-y-1.5 mb-4" role="radiogroup" aria-label="Role">
          <RoleRow
            label="Editor"
            description="Can edit, save, share, and download."
            value="editor"
            selected={role === 'editor'}
            onSelect={setRole}
          />
          <RoleRow
            label="Viewer"
            description="View only — can't edit or save."
            value="viewer"
            selected={role === 'viewer'}
            onSelect={setRole}
          />
        </div>

        <p className="text-[11px] text-gray-500 leading-relaxed mb-4">
          They'll receive an email with an Accept link. Invitations expire after 7 days.
        </p>

        {error && (
          <div
            className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 mb-3"
            data-testid="invite-error"
          >
            {error.message}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={sending || !isProbablyEmail(email)}
            data-testid="invite-send-btn"
          >
            {sending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Send Invite
          </Button>
        </div>
      </form>
    </div>
  );
}

function RoleRow({ label, description, value, selected, onSelect }) {
  return (
    <label
      className={[
        'flex items-start gap-3 p-2.5 rounded-md cursor-pointer border',
        selected ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-100 hover:bg-gray-50',
      ].join(' ')}
    >
      <input
        type="radio"
        name="invite-role"
        value={value}
        checked={selected}
        onChange={() => onSelect(value)}
        className="mt-1"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-[11px] text-gray-500 leading-snug">{description}</p>
      </div>
    </label>
  );
}
